import Keycloak, {
  KeycloakFlow,
  KeycloakOnLoad,
  KeycloakPkceMethod,
} from "keycloak-js";
import { emailFromClaims, IdentityClaims } from "./IdentityClaim";
import {
  getCurrentPublicUrl,
  toAbsolutePublicUrl,
  toAppPathname,
} from "./PublicUrl";

const keycloakConfig = {
  url: process.env.REACT_APP_KEYCLOAK_URL as string,
  realm: process.env.REACT_APP_KEYCLOAK_REALM as string,
  clientId: process.env.REACT_APP_KEYCLOAK_REALM_CLIENT_ID as string,
};

const keycloak = new Keycloak(keycloakConfig);
const e2eMode =
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_E2E_MODE === "true";
const e2eEmail = process.env.REACT_APP_E2E_USER_EMAIL || "john.doe@gmail.com";

const parseBoolean = (value: string | undefined, defaultValue: boolean): boolean =>
  value === undefined ? defaultValue : value.trim().toLowerCase() === "true";

const e2eToken: IdentityClaims = {
  email: e2eEmail,
  preferred_username: e2eEmail,
};

/** Initializes Keycloak and restores the route requested before login. */
const initKeycloak = (onAuthenticatedCallback: () => void) => {
  if (e2eMode) {
    onAuthenticatedCallback();
    return;
  }

  keycloak
    .init({
      onLoad: process.env.REACT_APP_KEYCLOAK_ONLOAD as KeycloakOnLoad,
      silentCheckSsoRedirectUri: toAbsolutePublicUrl("/silent-check-sso.html"),
      pkceMethod: process.env.REACT_APP_KEYCLOAK_PKCE_METHOD as KeycloakPkceMethod,
      flow: process.env.REACT_APP_KEYCLOAK_FLOW as KeycloakFlow,
      silentCheckSsoFallback: parseBoolean(
        process.env.REACT_APP_KEYCLOAK_CHECKSSO_FALLBACK,
        true,
      ),
      checkLoginIframe: parseBoolean(
        process.env.REACT_APP_KEYCLOAK_CHECKSSO_LOGIN_IFRAME,
        true,
      ),
    })
    .then(() => {
      const postLoginRedirectUri = localStorage.getItem("postLoginRedirectUri");
      if (postLoginRedirectUri) {
        localStorage.removeItem("postLoginRedirectUri");
        window.location.href = toAbsolutePublicUrl(
          toAppPathname(postLoginRedirectUri),
        );
        return;
      }

      onAuthenticatedCallback();
    })
    .catch(console.error);
};

const doLogin = (): Promise<void> => {
  if (e2eMode) {
    return Promise.resolve();
  }

  return keycloak.login({ redirectUri: getCurrentPublicUrl() });
};

const doLogout = (): Promise<void> => {
  if (e2eMode) {
    return Promise.resolve();
  }

  return keycloak.logout({ redirectUri: toAbsolutePublicUrl("/Home") });
};

const getToken = (): string | undefined =>
  e2eMode ? "saturne-e2e-token" : keycloak.token;

const getValidToken = async (): Promise<string | undefined> => {
  if (e2eMode) {
    return getToken();
  }

  if (!isAuthenticated()) {
    return undefined;
  }

  try {
    await keycloak.updateToken(30);
    return keycloak.token;
  } catch {
    await doLogin();
    return undefined;
  }
};

const getTokenParsed = (): IdentityClaims | undefined =>
  (e2eMode ? e2eToken : keycloak.tokenParsed) as IdentityClaims | undefined;

const isAuthenticated = (): boolean => e2eMode || Boolean(keycloak.token);

const getUsername = (): string | undefined => {
  const username = getTokenParsed()?.preferred_username;
  return typeof username === "string" && username.trim()
    ? username.trim()
    : getEmail();
};

const getEmail = (): string | undefined =>
  emailFromClaims(getTokenParsed());

const hasRole = (roles: string[]): boolean =>
  e2eMode || roles.some((role) => keycloak.hasRealmRole(role));

const getKC = () => keycloak;

const updateToken = (successCallback: () => unknown): Promise<unknown> => {
  if (e2eMode) {
    return Promise.resolve(successCallback());
  }

  return keycloak.updateToken(300).then(successCallback).catch(doLogin);
};

const UserService = {
  initKeycloak,
  doLogin,
  doLogout,
  isAuthenticated,
  getToken,
  getValidToken,
  getTokenParsed,
  getUsername,
  getEmail,
  hasRole,
  getKC,
  updateToken,
};

export default UserService;
